import type { PriceListKit } from "../../api/admin";
import { formatMoney, formatQuantity } from "../../api/money";

/** "100 EN · DN100 · 6 lines · K1 R6 941,56". */
export function kitSummary(kit: PriceListKit): string {
  const k1 = kit.k1 !== null ? `K1 ${formatMoney(kit.k1)}` : "K1 not priced: a code is missing";
  return [kit.size_label, `DN${kit.dn_mm}`, `${kit.lines} lines`, k1].join(" · ");
}

/** A version's service kits, read-only (decision 8): one block per size with
 * its standard lines, list and net prices, the labour line marked, and the
 * pilot options. There is nothing to edit here: a kit changes only when a new
 * version of the supplier's workbook is uploaded and put in force. */
export function ServiceKitsPanel({ kits }: { kits: PriceListKit[] }) {
  if (kits.length === 0) {
    return null;
  }
  return (
    <section className="card-body" aria-label="Service kits">
      <h3 className="section-label">Service kits</h3>
      <p className="muted-note">
        Read-only: the kits change only when a new version of the workbook is uploaded. K1 is what a pre-issued BOQ is
        priced at for the kit, before travelling and the pilot.
      </p>
      {kits.map((kit) => (
        <details key={kit.kit_id} className="kit-block">
          <summary>{kitSummary(kit)}</summary>
          <table className="data-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Part</th>
                <th className="num">Qty</th>
                <th className="num">List</th>
                <th className="num">Net</th>
                <th aria-label="Notes" />
              </tr>
            </thead>
            <tbody>
              {kit.standard.map((line) => (
                <tr key={line.seq}>
                  <td className="mono">{line.item_code}</td>
                  <td>{line.description ?? "—"}</td>
                  <td className="num">{formatQuantity(line.qty)}</td>
                  <td className="num">{formatMoney(line.list_price)}</td>
                  <td className="num">{formatMoney(line.net)}</td>
                  <td>
                    {line.labour ? <span className="kit-labour">Labour</span> : null}
                    {line.in_version ? null : <span className="line-warn">Not in this version</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {kit.pilot_options.length > 0 ? (
            <p className="muted-note">
              Pilot options:{" "}
              {kit.pilot_options.map((pilot) => `${pilot.model} ${pilot.item_code} ${formatMoney(pilot.list_price)}`).join("; ")}
            </p>
          ) : null}
        </details>
      ))}
    </section>
  );
}
